import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import useFetch from "utils/hooks/useFetch";
import Swal from "sweetalert2";
import {
  GET_TICKET_BY_ID,
  GET_PRICE_CALCULATION,
  GET_ALL_PAYMENT_CHANNELS,
  CREATE_BOOKING,
  CHECK_TICKET_STOCK,
  golf_pass_api,
  GET_POLO_JERSEY_SIZES,
} from "api";

import DateIcon from "pages/assets/icons/date";
import LocationIcon from "pages/assets/icons/location";

import NumberInput from "../components/QtyInput";
import ParticipantList from "../components/ParticipantList";
import PaymentMethods from "../components/PaymentMethods";
import PriceDetails from "../components/PriceDetails";
import { LoadingContext } from "components/layout/MainContainer";

import formatReferralCodes from "../helpers/formatReferralCodes";
import formatParticipants from "../helpers/formatParticipants";
import validateForm from "../helpers/validateForm";

// Images & Icons
import home_banner_desktop from "assets/images/home_banner_desktop.jpg";

function TicketDetails() {
  const navigate = useNavigate();
  const { isLoading, setIsLoading } = useContext(LoadingContext);

  const { t } = useTranslation();
  const { id } = useParams();
  const { data: ticket, fetch: getTicket } = useFetch(GET_TICKET_BY_ID);
  const { data: ticketStock, fetch: checkTicketStock } =
    useFetch(CHECK_TICKET_STOCK);
  const {
    data: priceCalculation,
    error: priceCalculationError,
    fetch: getPriceCalculation,
  } = useFetch(GET_PRICE_CALCULATION);
  const { data: paymentChannels, fetch: getAllPaymentChannels } = useFetch(
    GET_ALL_PAYMENT_CHANNELS,
  );
  const {
    data: bookingResult,
    error: createBookingError,
    fetch: createBooking,
  } = useFetch(CREATE_BOOKING);
  const { error: getPoloJerseySizesError, fetch: getPoloJerseySizes } =
    useFetch(GET_POLO_JERSEY_SIZES, false, golf_pass_api);

  const [qty, setQty] = useState(0);
  const [selectedPaymentMethod, selectPaymentMethod] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isRegistrationFormValid, setIsRegistrationFormValid] = useState(true);
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] = useState(false);
  const [jerseySizeOptions, setJerseySizeOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    getTicket({ params: { id } });
    getAllPaymentChannels();
    checkTicketStock({
      data: { qty },
      params: { id },
    });
    getPoloJerseySizes().then((res) => {
      if (res && res.success) {
        const { poloJerseySizes } = res || {};
        let sizeOpts = poloJerseySizes.map((size) => {
          return { name: size.name, value: size.id, stock: size.stock };
        });
        setJerseySizeOptions(sizeOpts);
      }
    });
  }, []);

  // Hardcoded: fixed qty for "scramble" ticket
  useEffect(() => {
    if (ticket && ticket.id) {
      if (ticket.is_group_ticket) {
        if (
          ticketStock &&
          ticketStock.stock &&
          parseInt(ticketStock.stock) >= 4
        ) {
          setQty(4);
        } else {
          setQty(0);
        }
      }
    }
  }, [ticket, ticketStock]);

  useEffect(() => {
    setIsPrivacyPolicyChecked(false);
  }, [qty, selectedPaymentMethod]);

  useEffect(() => {
    if (selectedPaymentMethod && qty && qty > 0) {
      // Get referral codes
      const referralCodes = formatReferralCodes(formValues);
      getPriceCalculation({
        data: {
          payment_channel_id: selectedPaymentMethod,
          qty,
          referral_codes: referralCodes,
        },
        params: {
          id,
        },
      });
    }
  }, [selectedPaymentMethod, qty]);

  useEffect(() => {
    selectPaymentMethod(null);
  }, [formValues]);

  useEffect(() => {
    let errResp = null;
    if (priceCalculationError) {
      errResp =
        priceCalculationError &&
        priceCalculationError.data &&
        priceCalculationError.data.message;
    }
    if (createBookingError) {
      errResp =
        createBookingError &&
        createBookingError.data &&
        createBookingError.data.message;
    }
    if (getPoloJerseySizesError) {
      errResp =
        getPoloJerseySizesError &&
        getPoloJerseySizesError.data &&
        getPoloJerseySizesError.data.message;
    }
    if (errResp) {
      Swal.fire({
        title: "Error",
        text: errResp,
        icon: "error",
      }).then(() => {
        window.location.reload();
      });
    }

    if (bookingResult) {
      setIsLoading(false);
      navigate(`/payment/qr?transaction_id=${bookingResult.transaction_id}`);
    }
  }, [
    createBookingError,
    bookingResult,
    priceCalculationError,
    getPoloJerseySizesError,
  ]);

  const validateRegistrationForm = () => {
    const requiredFields = [
      "customer-name",
      "customer-email",
      "phone-number",
      // "company-name",
      // "customer-division",
      "customer-phcode",
      "gender",
      "jersey-size",
    ];

    const result = validateForm(formValues, requiredFields);
    setFormErrors(result.errors);
    setIsRegistrationFormValid(result.isValid);
    return result.isValid;
  };

  const handleSubmit = () => {
    if (validateRegistrationForm()) {
      setIsLoading(true);

      let participants = formatParticipants(formValues, qty).map(
        (participant) => {
          const match = jerseySizeOptions.find(
            (opt) => opt.value === +participant.jersey_size,
          );

          return {
            ...participant,
            jersey_size_id: participant.jersey_size,
            jersey_size: match?.name || "",
          };
        },
      );

      createBooking({
        data: {
          ticket_id: id,
          mpayment_channel_id: selectedPaymentMethod,
          participants,
        },
      });
    }
  };

  return (
    <div>
      <section>
        <div className="relative">
          {/* home banner desktop size */}
          <img
            // src="https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/1c0610da06e3edba525e31bbf4bf526fa276d28803c039a64992d024874448b71b02826c5169acbf1c7c61b0dd764488a8a0bf8cd98872c2474650c847a0ffe234f1c22d82f685685cc5bfa38d5bd67b3cfa88080b46a965d68d693de5546d5439ef6d2d5800e97258a00974dc45dfebf67e847305ed821e943f/file-1736775970398.jpg"
            src={home_banner_desktop}
            alt="main"
            className="object-cover object-center w-full max-h-40 sm:max-h-60"
          />
          <div className="absolute top-0 bottom-0 left-0 right-0 text-white bg-black/60">
            <div className="flex flex-col items-center justify-center h-full gap-4 app-main-container sm:gap-8">
              <p className="max-w-md px-8 text-lg font-semibold text-center sm:text-3xl sm:font-bold app-animation-translate-start">
                {t("ticketDetails")}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="space-y-10 app-main-container md:space-y-12">
          <div className="text-xl md:text-3xl">{ticket && ticket.title}</div>
          <div className="space-y-4 md:space-y-6">
            <div className="flex gap-4">
              <div>
                <DateIcon bgFill={"#0F4C81"} fill={"#0F4C81"} size={50} />
              </div>
              <div className="space-y-0.5">
                <div className="text-base md:text-xl">
                  {ticket && moment(ticket.date).format("DD MMMM YYYY")}
                </div>
                <div className="text-sm font-light md:text-base">
                  {ticket && moment(ticket.date).format("dddd, hh:mma")}
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div>
                <LocationIcon bgFill={"#0F4C81"} fill={"#0F4C81"} size={50} />
              </div>
              <div className="space-y-0.5">
                <div className="text-base md:text-xl">
                  {ticket && ticket.address_title}
                </div>
                <div className="text-sm font-light md:text-base">
                  {ticket && ticket.address_details}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="mb-4 text-base font-bold md:text-xl">
              {t("ticketDescription")}
            </div>
            <div
              className="text-base md:text-xl custom-list"
              dangerouslySetInnerHTML={{ __html: ticket && ticket.description }}
            ></div>
          </div>
          <div>
            <div className="mb-4 text-base font-bold md:text-xl">
              {t("qty")}
            </div>
            <NumberInput
              setQty={setQty}
              qty={qty}
              maxQty={
                ticketStock &&
                ticketStock.stock &&
                parseInt(ticketStock.stock) <= 2
                  ? parseInt(ticketStock.stock)
                  : 2
              }
              disabled={
                (ticket && ticket.is_group_ticket) ||
                (ticketStock &&
                  ticketStock.stock &&
                  parseInt(ticketStock.stock) < 1)
              }
            />
            <div className="w-full p-2 mt-2 text-xs leading-5 border border-2 rounded-lg text-app-blue-light border-app-grey bg-app-blue sm:text-sm">
              {t("qtyNote")}
            </div>
            {(ticket &&
              !ticket.is_group_ticket &&
              ticketStock &&
              ticketStock.stock &&
              parseInt(ticketStock.stock) > 0) ||
            (ticket &&
              ticket.is_group_ticket &&
              ticketStock &&
              ticketStock.stock &&
              parseInt(ticketStock.stock) > 3) ? (
              // <div className='pt-2 text-xs font-bold md:text-sm text-app-blue'>{ticketStock && ticketStock.stock || 0} {t('availableStock')}</div>
              <></>
            ) : (
              <div className="pt-2 text-xs font-bold md:text-sm text-amber-700">
                {t("unavailableStock")}
              </div>
            )}
          </div>
          {(qty && qty > 0 && (
            <div>
              <div className="mb-4 text-base font-bold md:text-xl">
                {t("participants")}
              </div>
              <ParticipantList
                formValues={formValues}
                setFormValues={setFormValues}
                qty={qty}
                isRegistrationFormValid={isRegistrationFormValid}
                setIsRegistrationFormValid={setIsRegistrationFormValid}
                genderOptions={[
                  { name: "Male", value: "Male" },
                  { name: "Female", value: "Female" },
                ]}
                jerseySizeOptions={jerseySizeOptions}
                formErrors={formErrors}
              />
            </div>
          )) ||
            null}
          {(qty && qty > 0 && (
            <div>
              <div className="mb-4 text-base font-bold md:text-xl">
                {t("choosePaymentMethod")}
              </div>
              {/* <div className="w-full p-2 mb-4 text-xs leading-5 text-white bg-red-500 border border-2 border-red-700 rounded-lg sm:text-sm">
                {t("qrisPaymentNote")}
              </div> */}
              <PaymentMethods
                selectPaymentMethod={selectPaymentMethod}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentMethods={paymentChannels}
                priceDetails={priceCalculation}
              />
            </div>
          )) ||
            null}
          {(qty && qty > 0 && selectedPaymentMethod && priceCalculation && (
            <div>
              <div className="mb-4 text-base font-bold md:text-xl">
                {t("totalPrice")}
              </div>
              {ticket && ticket.is_group_ticket && (
                <div className="w-full p-2 mb-4 text-xs leading-5 border border-2 rounded-lg sm:text-sm bg-emerald-200/60 border-emerald-200">
                  {t("scrambleNote")}
                </div>
              )}
              <PriceDetails priceDetails={priceCalculation} />
              <div className="flex items-start">
                <input
                  checked={isPrivacyPolicyChecked}
                  type="checkbox"
                  onChange={() =>
                    setIsPrivacyPolicyChecked(!isPrivacyPolicyChecked)
                  }
                  className="mt-1 mr-2 scale-125"
                />
                <span className="text-xs sm:text-sm">
                  {t("privacyPolicyAgreement")}
                  <a href="/privacy-policy" target="_blank">
                    <u>{t("privacyPolicy")}</u>
                  </a>
                  .
                </span>
              </div>
            </div>
          )) ||
            null}
          {(qty && qty > 0 && selectedPaymentMethod && priceCalculation && (
            <div className="flex justify-center md:justify-end">
              <button
                onClick={handleSubmit}
                className="px-12 py-2 text-sm text-white rounded-md bg-app-blue sm:text-base disabled:opacity-50"
                disabled={isPrivacyPolicyChecked === false}
              >
                {t("continuePayment")}
              </button>
            </div>
          )) ||
            null}
        </div>
      </section>
    </div>
  );
}

export default TicketDetails;
